// Pure calculation helpers. All views read from these.

import { applyScenario } from "./scenario";

export const months = (data) => data?.meta?.months ?? [];

// Per-project totals
export const projectRawTotal = (project) =>
  project.scopes.reduce(
    (sum, s) => sum + Object.values(s.revenue || {}).reduce((a, b) => a + (b || 0), 0),
    0
  );

export const projectWeightedTotal = (project) =>
  project.scopes.reduce(
    (sum, s) =>
      sum +
      (s._originalProbability ?? s.probability ?? 1) *
        Object.values(s.revenue || {}).reduce((a, b) => a + (b || 0), 0),
    0
  );

// Per-project monthly revenue (raw + weighted)
export const projectMonthlyRaw = (project, months) => {
  const out = Object.fromEntries(months.map((m) => [m, 0]));
  for (const s of project.scopes) {
    for (const m of months) out[m] += s.revenue?.[m] ?? 0;
  }
  return out;
};

export const projectMonthlyWeighted = (project, months) => {
  const out = Object.fromEntries(months.map((m) => [m, 0]));
  for (const s of project.scopes) {
    const p = s._originalProbability ?? s.probability ?? 1;
    for (const m of months) out[m] += (s.revenue?.[m] ?? 0) * p;
  }
  return out;
};

// Firm-wide monthly revenue
export const firmMonthly = (data, weighted = false) => {
  const m = months(data);
  const out = Object.fromEntries(m.map((x) => [x, 0]));
  for (const p of data.projects) {
    const month = weighted ? projectMonthlyWeighted(p, m) : projectMonthlyRaw(p, m);
    for (const k of m) out[k] += month[k];
  }
  return out;
};

export const firmGrandTotal = (data, weighted = false) =>
  data.projects.reduce(
    (s, p) => s + (weighted ? projectWeightedTotal(p) : projectRawTotal(p)),
    0
  );

// Revenue by stage (firm-wide totals) — summed over the VISIBLE window
// (`monthsArr`, defaulting to data.meta.months) so the figures move with the
// period selector like every other total on the page.
export const revenueByStage = (data, weighted = false, monthsArr = null) => {
  const m = monthsArr ?? months(data);
  const stages = {};
  for (const p of data.projects) {
    for (const s of p.scopes) {
      const total = m.reduce((a, mo) => a + (s.revenue?.[mo] ?? 0), 0);
      const v = weighted ? total * (s._originalProbability ?? s.probability ?? 1) : total;
      stages[s.stage] = (stages[s.stage] || 0) + v;
    }
  }
  return Object.entries(stages).map(([stage, value]) => ({ stage, value }));
};

// Canonical stage rollup — maps free-text scope labels onto the firm's
// canonical stages (data.meta.stages) so the Overview chart shows at most
// canonical + "Other" bars, no matter how many custom labels exist.
const STAGE_KEYWORDS = {
  Feasibility: ["feasibility"],
  Concept: ["concept"],
  "Preliminary Design": ["preliminary"],
  "Developed Design": ["developed"],
  "Detailed Design": ["detailed"],
  Procurement: ["procurement", "tender"],
  "Site Observation": ["observation", "site obs"],
};

export const rollupStage = (label, canonical) => {
  const l = (label || "").toLowerCase().trim();
  if (!l) return "Other";
  for (const c of canonical) {
    if (c.toLowerCase() === l) return c;
  }
  for (const c of canonical) {
    const keys = STAGE_KEYWORDS[c] || [c.toLowerCase()];
    if (keys.some((k) => l.includes(k))) return c;
  }
  return "Other";
};

// Monthly revenue split by canonical stage (stacked-bar shape). Only stages
// principals actually used are returned — canonical order, "Other" last.
// -> { rows: [{ month, [stage]: $ }], stages: [names present] }
export const revenueByStageMonthly = (data, monthsArr) => {
  const canonical = data?.meta?.stages || Object.keys(STAGE_KEYWORDS);
  const m = monthsArr ?? data?.meta?.months ?? [];
  const rows = m.map((mo) => ({ month: mo }));
  const present = new Set();
  for (const p of data.projects) {
    for (const s of p.scopes) {
      const key = rollupStage(s.stage, canonical);
      m.forEach((mo, i) => {
        const v = s.revenue?.[mo] ?? 0;
        if (!v) return;
        rows[i][key] = (rows[i][key] || 0) + v;
        present.add(key);
      });
    }
  }
  const stages = [...canonical, "Other"].filter((k) => present.has(k));
  return { rows, stages };
};

// Revenue rolled up to canonical stages + "Other" (max canonical.length + 1 bars),
// ordered by the canonical stage sequence with Other last. Zero buckets dropped.
export const revenueByStageRolled = (data, weighted = false) => {
  const canonical = data?.meta?.stages || Object.keys(STAGE_KEYWORDS);
  const buckets = {};
  for (const p of data.projects) {
    for (const s of p.scopes) {
      const total = Object.values(s.revenue || {}).reduce((a, b) => a + (b || 0), 0);
      const v = weighted ? total * (s._originalProbability ?? s.probability ?? 1) : total;
      const key = rollupStage(s.stage, canonical);
      buckets[key] = (buckets[key] || 0) + v;
    }
  }
  const order = [...canonical, "Other"];
  return order
    .filter((k) => buckets[k])
    .map((stage) => ({ stage, value: buckets[stage] }));
};

// Contract certainty split — RAW totals grouped by Yes / Tentative / No,
// summed over the VISIBLE window (`monthsArr`, defaulting to data.meta.months)
// so the split follows the period selector instead of reading every stored
// month. Classification is PER SCOPE ROW so a mixed project (some Confirmed,
// some Tentative scopes) is split correctly instead of being flagged all-or-
// nothing by the derived project-level flag. Sub-consultant rows are
// pass-through money and skipped here (they contribute to the owning
// project's totals but don't sit in a certainty bucket of their own).
//   The Overview's Contract status donut does NOT use this helper — it must
//   reconcile with the Backlog / Pipeline tiles, so it reads
//   backlogPipelineTotals() instead (subs included, scenario-independent).
export const revenueByContract = (data, weighted = false, monthsArr = null) => {
  const m = monthsArr ?? months(data);
  const buckets = { Yes: 0, Tentative: 0, No: 0 };
  for (const p of data.projects) {
    for (const s of p.scopes || []) {
      if (s.isSubConsultant) continue;
      const key = ["Yes", "Tentative", "No"].includes(s.contract) ? s.contract : "No";
      const total = m.reduce((a, mo) => a + (s.revenue?.[mo] ?? 0), 0);
      const v = weighted ? total * (s._originalProbability ?? s.probability ?? 1) : total;
      buckets[key] += v;
    }
  }
  return Object.entries(buckets).map(([k, v]) => ({ name: k, value: v }));
};

// Capacity: load per (person, month)
export const capacityMatrix = (data) => {
  const m = months(data);
  const matrix = {};
  for (const person of data.people) {
    matrix[person.id] = Object.fromEntries(m.map((x) => [x, 0]));
  }
  for (const proj of data.projects) {
    for (const a of proj.allocations || []) {
      if (!matrix[a.personId]) continue;
      for (const month of m) {
        matrix[a.personId][month] += a.load?.[month] ?? 0;
      }
    }
  }
  return matrix; // { personId: { month: load } }
};

// What projects eat a person's time in a given month?
// A person can appear more than once in a project's allocations if they were
// removed and re-added — sum the loads so each project shows up once.
export const personProjectsInMonth = (data, personId, month) => {
  const byProject = new Map();
  for (const proj of data.projects) {
    for (const a of proj.allocations || []) {
      if (a.personId !== personId) continue;
      const load = a.load?.[month] ?? 0;
      if (load <= 0) continue;
      const prev = byProject.get(proj.id);
      if (prev) prev.load += load;
      else byProject.set(proj.id, { projectId: proj.id, projectName: proj.name, load });
    }
  }
  return Array.from(byProject.values()).sort((a, b) => b.load - a.load);
};

// Aggregate utilisation per person across months.
// `leaveFrac` ({ personId: { "YYYY-MM": fraction } }) — leave is ADDITIVE on
// top of project load (firm decision, 2026-09-07: utilisation counts leave),
// so a month booked to 80% with a full week away counts as 105% in EVERY
// measure here: `avg`, the `over` month count, `peak` and `spareTotal`. This
// is the same rule the matrix cells and their colour bands use. Omit the
// argument and all four measures are booked-load only.
export const personSummary = (data, leaveFrac = null) => {
  const m = months(data);
  const matrix = capacityMatrix(data);
  return data.people.map((p) => {
    const monthly = matrix[p.id];
    const totals = m.map((mo) => (monthly[mo] || 0) + (leaveFrac?.[p.id]?.[mo] || 0));
    const avg = totals.reduce((a, b) => a + b, 0) / m.length;
    const over = totals.filter((l) => l > 1.0001).length;
    const peak = Math.max(...totals, 0);
    const spareTotal = totals.reduce((a, b) => a + Math.max(0, 1 - b), 0);
    return { ...p, monthly, avg, over, peak, spareTotal };
  });
};

// Overall firm KPIs.
// `countSource` (optional) supplies the project list used for the LIVE /
// COMPLETED head-counts. Pass the raw dataset so the count answers "how many
// projects are on the go" instead of moving with the Confirmed/Forecast
// toggle — the scenario filter drops projects left with no qualifying scope
// row, which made the tile read 24 under Confirmed and 27 under Forecast.
// `leaveFrac` ({ personId: { "YYYY-MM": fraction } }) is added to each cell so
// utilisation and over-allocation count leave the same way the capacity matrix
// does (firm decision, 2026-09-07). Omit it for booked-load only.
export const firmKpis = (data, countSource, leaveFrac = null) => {
  const m = months(data);
  const src = countSource ?? data;
  const countable = src?.projects ?? [];
  const liveProjects = countable.filter((p) => (p.status || "") !== "Completed");
  // Utilisation and over-allocation count ALL LIVE WORK, scenario-independent
  // and consistent with the Live projects tile — only money figures follow the
  // Confirmed/Forecast toggle. The window still applies (meta.months).
  const matrix = capacityMatrix({
    meta: data.meta,
    people: src?.people ?? data.people,
    projects: liveProjects,
  });
  let overPersonMonths = 0;
  let totalLoad = 0;
  let totalCells = 0;
  for (const person of src?.people ?? data.people) {
    for (const month of m) {
      const l = (matrix[person.id][month] || 0) + (leaveFrac?.[person.id]?.[month] || 0);
      totalLoad += l;
      totalCells += 1;
      if (l > 1.0001) overPersonMonths += 1;
    }
  }
  return {
    totalRaw: firmGrandTotal(data, false),
    totalWeighted: firmGrandTotal(data, true),
    // Live projects only — Completed work still counts in every money figure,
    // but the headcount tile answers "how much is on the go right now".
    activeProjects: liveProjects.length,
    completedProjects: countable.length - liveProjects.length,
    peopleCount: (src?.people ?? data.people).length,
    avgUtilisation: totalCells ? totalLoad / totalCells : 0,
    overPersonMonths,
  };
};

// Top N projects by raw total
export const topProjects = (data, n = 5) =>
  [...data.projects]
    .map((p) => ({ ...p, raw: projectRawTotal(p), weighted: projectWeightedTotal(p) }))
    .sort((a, b) => b.raw - a.raw)
    .slice(0, n);

// Principal aggregates
export const principalSummary = (data) => {
  const m = months(data);
  return data.principals.map((pr) => {
    const projects = data.projects.filter((p) => p.principalId === pr.id);
    const totalRaw = projects.reduce((s, p) => s + projectRawTotal(p), 0);
    const totalWeighted = projects.reduce((s, p) => s + projectWeightedTotal(p), 0);
    const monthlyLoad = Object.fromEntries(m.map((x) => [x, 0]));
    for (const p of projects) {
      for (const a of p.allocations || []) {
        for (const month of m) monthlyLoad[month] += a.load?.[month] ?? 0;
      }
    }
    return { ...pr, projects, totalRaw, totalWeighted, monthlyLoad };
  });
};

// Allocation tables
export const allocationByMonth = (data, weighted = false) => {
  const m = months(data);
  const out = firmMonthly(data, weighted);
  return m.map((mo) => ({ month: mo, value: out[mo] }));
};

export const allocationByPrincipal = (data, weighted = false) =>
  data.principals.map((pr) => {
    const projs = data.projects.filter((p) => p.principalId === pr.id);
    const value = projs.reduce(
      (s, p) => s + (weighted ? projectWeightedTotal(p) : projectRawTotal(p)),
      0
    );
    return { ...pr, value };
  });

// Backlog (contract=Yes) vs Pipeline (contract=Tentative) per month, RAW $.
// Derived from the SAME scenario projection the Confirmed/Forecast totals
// use, so the two identities the firm relies on hold PER MONTH by
// construction, whatever the data does:
//     Backlog            = Confirmed total
//     Backlog + Pipeline = Forecast total
// Classification stays per scope row (a mixed project splits across both
// buckets) and sub-consultant rows are included, classified by their own
// contract status — Pipeline is therefore the incremental value the
// Tentative rows add on top of the signed position.
// Always sourced from the ORIGINAL dataset — independent of the toggle.
export const backlogPipelineMonthly = (rawData, monthsArr) => {
  const m = monthsArr ?? rawData?.meta?.months ?? [];
  if (!rawData) return m.map((mo) => ({ month: mo, backlog: 0, pipeline: 0 }));
  const confirmed = firmMonthly(applyScenario(rawData, "confirmed", m), false);
  const forecast = firmMonthly(applyScenario(rawData, "confirmed_tentative", m), false);
  return m.map((mo) => ({
    month: mo,
    backlog: confirmed[mo] || 0,
    pipeline: (forecast[mo] || 0) - (confirmed[mo] || 0),
  }));
};

// Backlog vs Pipeline totals (for KPI tiles).
export const backlogPipelineTotals = (rawData, monthsArr) => {
  const rows = backlogPipelineMonthly(rawData, monthsArr);
  return rows.reduce(
    (acc, r) => ({ backlog: acc.backlog + r.backlog, pipeline: acc.pipeline + r.pipeline }),
    { backlog: 0, pipeline: 0 }
  );
};

// Gross / sub-consultant / net per month.
//   total = every NON sub-consultant scope row (the gross fee to the client)
//   subs  = magnitude of the sub-consultant rows (stored negative, shown positive)
//   net   = total − subs (what the firm keeps)
// `data` is whatever the caller passes — pass the scenario-applied dataset to
// have the series follow the Confirmed/Forecast toggle.
export const grossNetSubMonthly = (data, monthsArr) => {
  const m = monthsArr ?? data?.meta?.months ?? [];
  return m.map((mo) => {
    let total = 0;
    let subs = 0;
    for (const p of data?.projects ?? []) {
      for (const s of p.scopes || []) {
        const v = s.revenue?.[mo] ?? 0;
        if (s.isSubConsultant) subs += Math.abs(v);
        else total += v;
      }
    }
    return { month: mo, total, subs, net: total - subs };
  });
};

// Invoiced (actuals) vs forecast per month, RAW $ — independent of scenario.
// Forecast = every scope's monthly fee; invoiced = every scope's invoiced map.
// Disbursements and time charge are just regular stage scopes now.
export const invoicedVsForecastMonthly = (rawData, monthsArr) => {
  const m = monthsArr ?? rawData?.meta?.months ?? [];
  const out = m.map((mo) => ({ month: mo, forecast: 0, invoiced: 0, hasInvoice: false }));
  for (const p of rawData?.projects ?? []) {
    let hasNew = false;
    for (const s of p.scopes || []) {
      m.forEach((mo, i) => { out[i].forecast += s.revenue?.[mo] ?? 0; });
      if (s.invoiced && Object.values(s.invoiced).some((v) => v)) hasNew = true;
    }
    // Legacy disbursements map — still supported until migration lands.
    const disb = p.disbursementsInvoiced || {};
    if (Object.values(disb).some((v) => v)) hasNew = true;
    if (hasNew) {
      for (const s of p.scopes || []) {
        m.forEach((mo, i) => {
          const v = Number(s.invoiced?.[mo]) || 0;
          if (v) { out[i].invoiced += v; out[i].hasInvoice = true; }
        });
      }
      m.forEach((mo, i) => {
        const v = Number(disb[mo]) || 0;
        if (v) { out[i].invoiced += v; out[i].hasInvoice = true; }
      });
    } else {
      // Legacy single-row actuals.
      const inv = p.actualInvoiced || {};
      m.forEach((mo, i) => {
        const v = Number(inv[mo]) || 0;
        if (v) { out[i].invoiced += v; out[i].hasInvoice = true; }
      });
    }
  }
  return out;
};

// Sustained over-allocation — people over 100% for `minStreak` consecutive
// months. Leave counts additively, like every other utilisation measure (firm
// decision, 2026-09-07): three straight months over the line is a coverage
// problem whether the overload comes from booked work or from absence.
// Renamed from `hiringSignal` at the firm's request — they would not hire off
// a few over-allocated months, so the alert names the condition, not a remedy.
export const overAllocationAlert = (data, minStreak = 3, leaveFrac = null) => {
  const m = months(data);
  const matrix = capacityMatrix(data);
  const out = [];
  for (const person of data.people) {
    let streak = 0;
    let maxStreak = 0;
    let startIdx = -1;
    let bestStart = -1;
    for (let i = 0; i < m.length; i += 1) {
      const load = (matrix[person.id][m[i]] || 0) + (leaveFrac?.[person.id]?.[m[i]] || 0);
      if (load > 1.0001) {
        if (streak === 0) startIdx = i;
        streak += 1;
        if (streak > maxStreak) { maxStreak = streak; bestStart = startIdx; }
      } else {
        streak = 0;
      }
    }
    if (maxStreak >= minStreak) {
      out.push({
        ...person,
        streak: maxStreak,
        fromMonth: m[bestStart],
        toMonth: m[bestStart + maxStreak - 1],
      });
    }
  }
  return out;
};

// Capacity band classification
export const bandFor = (load) => {
  if (!load || load <= 0.0001) return "empty";
  if (load > 1.0001) return "over";
  if (load >= 0.8) return "balanced";
  return "available";
};

// ---- Resourcing approval helpers ----
// Extra load (proposed minus approved) per person/month from pending rows.
export const capacityMatrixProposed = (data) => {
  const m = months(data);
  const extra = {};
  for (const person of data?.people || []) {
    extra[person.id] = Object.fromEntries(m.map((mo) => [mo, 0]));
  }
  for (const p of data?.projects || []) {
    for (const a of p.allocations || []) {
      if (a.status !== "proposed" || !extra[a.personId]) continue;
      for (const mo of m) {
        const d = (a.proposedValues?.[mo] || 0) - (a.approvedValues?.[mo] || 0);
        if (d) extra[a.personId][mo] += d;
      }
    }
  }
  return extra;
};

export const pendingApprovalsCount = (data) =>
  (data?.projects || []).reduce(
    (n, p) => n + (p.allocations || []).filter((a) => a.status === "proposed").length, 0);

export const pendingApprovalsList = (data) => {
  const out = [];
  for (const p of data?.projects || []) {
    for (const a of p.allocations || []) {
      if (a.status === "proposed") out.push({ project: p, alloc: a });
    }
  }
  return out;
};
